import { ChevronDown, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";

import { createSuperAdminOrganization } from "../config/url";
import { getConfigWithToken } from "../config/config";
import { US_STATES } from "../utils/usStates";
import classes from "./AddOrganizationDrawer.module.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = {
	adminEmail: "",
	adminFirstName: "",
	adminLastName: "",
	city: "",
	organizationName: "",
	state: "",
};

const AddOrganizationDrawer = ({ isOpen, onClose, onSuccess }) => {
	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");

	const [errors, setErrors] = useState({});
	const [form, setForm] = useState(emptyForm);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [serverError, setServerError] = useState("");

	const requestHeaders = useMemo(
		() => ({
			Accept: "application/json",
			"Content-Type": "application/json",
			...(token ? getConfigWithToken(token).headers : {}),
		}),
		[token],
	);

	useEffect(() => {
		if (!isOpen) return undefined;

		setForm(emptyForm);
		setErrors({});
		setServerError("");

		const onKeyDown = (event) => {
			if (event.key === "Escape") onClose();
		};

		document.addEventListener("keydown", onKeyDown);
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.body.style.overflow = "";
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const updateField = (field, value) => {
		setForm((previous) => ({ ...previous, [field]: value }));
		setErrors((previous) => {
			const next = { ...previous };
			delete next[field];
			return next;
		});
		setServerError("");
	};

	const validate = () => {
		const nextErrors = {};

		if (!form.organizationName.trim()) nextErrors.organizationName = "Organization name is required.";
		if (!form.city.trim()) nextErrors.city = "City is required.";
		if (!form.state.trim()) nextErrors.state = "State is required.";
		if (!form.adminFirstName.trim()) nextErrors.adminFirstName = "First name is required.";
		if (!form.adminLastName.trim()) nextErrors.adminLastName = "Last name is required.";

		if (!form.adminEmail.trim()) {
			nextErrors.adminEmail = "Email is required.";
		} else if (!EMAIL_REGEX.test(form.adminEmail.trim())) {
			nextErrors.adminEmail = "Enter a valid email address.";
		}

		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!validate()) return;

		setIsSubmitting(true);
		setServerError("");

		try {
			const response = await axios.post(
				createSuperAdminOrganization,
				{
					adminEmail: form.adminEmail.trim(),
					adminFirstName: form.adminFirstName.trim(),
					adminLastName: form.adminLastName.trim(),
					city: form.city.trim(),
					organizationName: form.organizationName.trim(),
					state: form.state.trim(),
				},
				{ headers: requestHeaders },
			);

			if (response.data?.success === false) {
				setServerError(response.data?.message || "Failed to create organization.");
				return;
			}

			toast.success(response.data?.message || "Organization created successfully.");
			onSuccess?.(response.data?.data);
			onClose();
		} catch (error) {
			setServerError(error.response?.data?.message || "Failed to create organization.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<button
				aria-label="Close add organization drawer"
				className={classes.backdrop}
				onClick={onClose}
				type="button"
			/>
			<aside
				aria-labelledby="add-organization-title"
				aria-modal="true"
				className={classes.drawer}
				role="dialog">
				<div className={classes.header}>
					<div className={classes.headerTop}>
						<h2 className={classes.title} id="add-organization-title">
							Add Organization
						</h2>
						<button aria-label="Close" className={classes.closeButton} onClick={onClose} type="button">
							<X size={18} />
						</button>
					</div>
					<p className={classes.subtitle}>Create a new organization on TeleTrace</p>
				</div>

				<form className={classes.form} onSubmit={handleSubmit}>
					<div className={classes.body}>
						<section className={classes.formSection}>
							<p className={classes.sectionHeading}>Organization Info</p>

							<label className={classes.field}>
								<span className={classes.label}>Organization Name</span>
								<input
									aria-required="true"
									className={`${classes.input} ${errors.organizationName ? classes.inputError : ""}`}
									onChange={(event) => updateField("organizationName", event.target.value)}
									type="text"
									value={form.organizationName}
								/>
								{errors.organizationName ? (
									<p className={classes.errorText}>{errors.organizationName}</p>
								) : null}
							</label>

							<div className={classes.gridTwo}>
								<label className={classes.field}>
									<span className={classes.label}>City</span>
									<input
										aria-required="true"
										className={`${classes.input} ${errors.city ? classes.inputError : ""}`}
										onChange={(event) => updateField("city", event.target.value)}
										type="text"
										value={form.city}
									/>
									{errors.city ? <p className={classes.errorText}>{errors.city}</p> : null}
								</label>

								<div className={classes.field}>
									<span className={classes.label}>State</span>
									<div className={classes.selectWrap}>
										<select
											aria-required="true"
											className={`${classes.select} ${!form.state ? classes.selectPlaceholder : ""} ${
												errors.state ? classes.inputError : ""
											}`}
											onChange={(event) => updateField("state", event.target.value)}
											value={form.state}>
											<option value="">Select</option>
											{US_STATES.map((stateOption) => (
												<option key={stateOption.value} value={stateOption.value}>
													{stateOption.label}
												</option>
											))}
										</select>
										<ChevronDown aria-hidden="true" className={classes.selectIcon} size={14} strokeWidth={2} />
									</div>
									{errors.state ? <p className={classes.errorText}>{errors.state}</p> : null}
								</div>
							</div>
						</section>

						<div aria-hidden="true" className={classes.sectionDivider} />

						<section className={classes.formSection}>
							<p className={classes.sectionHeading}>Admin Account</p>
							<p className={classes.sectionHint}>
								This person will be the first admin for the organization and will receive an invite to set
								up their account.
							</p>

							<div className={classes.gridTwoEqual}>
								<label className={classes.field}>
									<span className={classes.label}>First Name</span>
									<input
										aria-required="true"
										className={`${classes.input} ${errors.adminFirstName ? classes.inputError : ""}`}
										onChange={(event) => updateField("adminFirstName", event.target.value)}
										placeholder=""
										type="text"
										value={form.adminFirstName}
									/>
									{errors.adminFirstName ? (
										<p className={classes.errorText}>{errors.adminFirstName}</p>
									) : null}
								</label>

								<label className={classes.field}>
									<span className={classes.label}>Last Name</span>
									<input
										aria-required="true"
										className={`${classes.input} ${errors.adminLastName ? classes.inputError : ""}`}
										onChange={(event) => updateField("adminLastName", event.target.value)}
										placeholder=""
										type="text"
										value={form.adminLastName}
									/>
									{errors.adminLastName ? (
										<p className={classes.errorText}>{errors.adminLastName}</p>
									) : null}
								</label>
							</div>

							<label className={classes.field}>
								<span className={classes.label}>Email Address</span>
								<input
									aria-required="true"
									className={`${classes.input} ${errors.adminEmail ? classes.inputError : ""}`}
									onChange={(event) => updateField("adminEmail", event.target.value)}
									placeholder=""
									type="email"
									value={form.adminEmail}
								/>
								{errors.adminEmail ? <p className={classes.errorText}>{errors.adminEmail}</p> : null}
							</label>
						</section>

						{serverError ? <div className={classes.serverError}>{serverError}</div> : null}
					</div>

					<div className={classes.footer}>
						<button className={classes.cancelButton} onClick={onClose} type="button">
							Cancel
						</button>
						<button className={classes.submitButton} disabled={isSubmitting} type="submit">
							{isSubmitting ? "Creating..." : "Create Organization"}
						</button>
					</div>
				</form>
			</aside>
		</>
	);
};

export default AddOrganizationDrawer;
